type Point = { x: number; y: number };
type Triangle = [Point, Point, Point];

type SubmenuAim = {
  area: Triangle;
  pendingAction: VoidFunction | null;
  expiryTimeout: ReturnType<typeof setTimeout>;
};

const AIM_TIMEOUT_MS = 500;

const aimRef: { current: SubmenuAim | null } = { current: null };

const toAimArea = (apex: Point, submenuRect: DOMRect): Triangle => {
  const nearEdgeX =
    submenuRect.left >= apex.x ? submenuRect.left : submenuRect.right;
  return [
    apex,
    { x: nearEdgeX, y: submenuRect.top },
    { x: nearEdgeX, y: submenuRect.bottom },
  ];
};

const cross = (p: Point, a: Point, b: Point): number =>
  (p.x - b.x) * (a.y - b.y) - (a.x - b.x) * (p.y - b.y);

const isPointInTriangle = (point: Point, [a, b, c]: Triangle): boolean => {
  const signs = [cross(point, a, b), cross(point, b, c), cross(point, c, a)];
  return !(signs.some(sign => sign < 0) && signs.some(sign => sign > 0));
};

const stopAim = (): SubmenuAim | null => {
  const aim = aimRef.current;
  if (!aim) {
    return null;
  }
  clearTimeout(aim.expiryTimeout);
  document.removeEventListener('mousemove', handleMouseMove);
  aimRef.current = null;
  return aim;
};

const settleAim = (): void => {
  stopAim()?.pendingAction?.();
};

function handleMouseMove(event: MouseEvent): void {
  const aim = aimRef.current;
  if (aim && !isPointInTriangle(toPoint(event), aim.area)) {
    settleAim();
  }
}

export const toPoint = (event: MouseEvent | React.MouseEvent): Point => ({
  x: event.clientX,
  y: event.clientY,
});

/**
 * Starts tracking whether the pointer is heading from `apex` towards the open
 * submenu. While it stays within the triangle spanned by `apex` and the
 * submenu's near edge, actions passed to `runUnlessAimingAtSubmenu` are held
 * back for up to AIM_TIMEOUT_MS.
 */
export const startSubmenuAim = (apex: Point, submenuRect: DOMRect): void => {
  stopAim();
  document.addEventListener('mousemove', handleMouseMove);
  aimRef.current = {
    area: toAimArea(apex, submenuRect),
    pendingAction: null,
    expiryTimeout: setTimeout(settleAim, AIM_TIMEOUT_MS),
  };
};

/** The pointer reached the submenu: drop any held back action. */
export const cancelSubmenuAim = (): void => {
  stopAim();
};

/**
 * Runs `action` right away, unless the pointer is on its way to an open
 * submenu. Then it runs once the pointer leaves the aim triangle or the aim
 * times out. Returns a function that discards the held back action.
 */
export const runUnlessAimingAtSubmenu = (
  point: Point,
  action: VoidFunction
): VoidFunction => {
  const aim = aimRef.current;
  if (!aim || !isPointInTriangle(point, aim.area)) {
    stopAim();
    action();
    return () => {};
  }
  aim.pendingAction = action;
  return () => {
    if (aimRef.current?.pendingAction === action) {
      aimRef.current.pendingAction = null;
    }
  };
};
